import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { CytoElement, DNSNode, GodEnrichmentResult, GraphState } from '@/types/dns';

export interface ScanHistoryEntry {
  target: string;
  scannedAt: string; // ISO timestamp
  threatScore: number | null;
  jobId: string;
}

interface GraphActions {
  addElements: (elements: CytoElement[]) => void;
  setSelectedNode: (node: DNSNode | null) => void;
  setScanning: (scanning: boolean) => void;
  setScanError: (error: string | null) => void;
  setCurrentJobId: (jobId: string | null) => void;
  setScanProgress: (progress: number | null) => void;
  clearGraph: () => void;
  setLastScanResult: (result: GodEnrichmentResult, target: string, jobId: string) => void;
}

const _loadHistory = (): ScanHistoryEntry[] => {
  try {
    return JSON.parse(localStorage.getItem('dnsscience_scan_history') ?? '[]');
  } catch {
    return [];
  }
};

const initialState: GraphState = {
  elements: [],
  selectedNode: null,
  scanning: false,
  scanError: null,
  currentJobId: null,
  scanProgress: null,
  lastScanResult: null,
  lastScanTarget: null,
  scanHistory: _loadHistory(),
};

export const useGraphStore = create<GraphState & GraphActions>()(
  immer((set) => ({
    ...initialState,

    addElements: (incoming) =>
      set((state) => {
        const existingIds = new Set(state.elements.map((el) => el.data.id));
        for (const el of incoming) {
          if (!existingIds.has(el.data.id)) {
            state.elements.push(el);
            existingIds.add(el.data.id);
          }
        }
      }),

    setSelectedNode: (node) =>
      set((state) => {
        state.selectedNode = node;
      }),

    setScanning: (scanning) =>
      set((state) => {
        state.scanning = scanning;
      }),

    setScanError: (error) =>
      set((state) => {
        state.scanError = error;
      }),

    setCurrentJobId: (jobId) =>
      set((state) => {
        state.currentJobId = jobId;
      }),

    setScanProgress: (progress) =>
      set((state) => {
        state.scanProgress = progress;
      }),

    clearGraph: () =>
      set((state) => {
        state.elements = [];
        state.selectedNode = null;
        state.scanError = null;
        state.currentJobId = null;
        state.scanning = false;
        state.scanProgress = null;
        state.lastScanResult = null;
        state.lastScanTarget = null;
      }),

    setLastScanResult: (result, target, jobId) =>
      set((state) => {
        state.lastScanResult = result;
        state.lastScanTarget = target;
        // Prepend to history, keep last 20
        const score = (() => {
          const rs = (result as Record<string, unknown>).risk_score as Record<string, unknown> | undefined;
          const inner = (rs?.risk_score as Record<string, unknown> | undefined) ?? rs;
          const raw = inner?.score;
          return raw != null ? Number(raw) : null;
        })();
        const entry: ScanHistoryEntry = { target, scannedAt: new Date().toISOString(), threatScore: score, jobId };
        const next = [entry, ...(state.scanHistory ?? []).filter(h => h.target !== target)].slice(0, 20);
        state.scanHistory = next;
        try { localStorage.setItem('dnsscience_scan_history', JSON.stringify(next)); } catch {}
      }),
  })),
);
